import { useState, type FormEvent } from 'react'
import { toast } from 'sonner'
import { KeyRound } from 'lucide-react'
import { useAuth } from '@/context/AuthContext'
import { ApiError } from '@/lib/api'
import { Button, Field, PasswordInput } from '@/components/ui'
import { KaproLogo } from '@/components/Logo'
import { useT } from '@/i18n/I18nProvider'

/**
 * Shown once, to somebody who signed in with an activation code.
 *
 * Not a route. It replaces the whole application until the password is set,
 * because the code they used was read out to them by another person — and until
 * it is replaced, that person can sign in as them. The server enforces the same
 * thing independently (`EnsurePasswordIsSet` returns 423 for everything else),
 * so this screen is the explanation rather than the lock.
 *
 * There is deliberately no "skip" and no way out except signing out. A worker
 * who postpones this is a worker whose supervisor keeps their credential.
 */
export function SetPasswordScreen() {
  const { user, setOwnPassword, logout } = useAuth()
  const t = useT()

  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    setError(null)

    // Checked here so the mismatch lands on the field rather than as a toast
    // after a round trip; the server checks it too.
    if (password !== confirm) {
      setError(t('profile.password.mismatch'))
      return
    }

    setSubmitting(true)

    try {
      await setOwnPassword(password)
      toast.success(t('profile.setPassword.success'))
    } catch (err) {
      setError(
        err instanceof ApiError
          ? (err.fieldError('password') ?? err.message)
          : t('profile.setPassword.failed'),
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="flex min-h-dvh items-center justify-center px-6 py-10">
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center text-center">
          <KaproLogo size="lg" />

          <span className="bg-brand-100 text-brand-700 dark:bg-brand-900/50 dark:text-brand-300 mt-6 flex size-12 items-center justify-center rounded-2xl">
            <KeyRound className="size-6" aria-hidden />
          </span>

          <h1 className="mt-4 text-xl font-semibold">{t('profile.setPassword.title')}</h1>

          <p className="muted mt-2 text-sm">{t('profile.setPassword.intro')}</p>
        </div>

        <form onSubmit={handleSubmit} className="mt-8 space-y-4" noValidate>
          <Field
            label={t('profile.password.new')}
            required
            hint={t('profile.password.new.hint')}
          >
            <PasswordInput
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              minLength={8}
              required
              autoFocus
              aria-invalid={error ? true : undefined}
            />
          </Field>

          <Field label={t('profile.password.confirm')} required>
            <PasswordInput
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              required
              enterKeyHint="go"
              aria-invalid={error ? true : undefined}
            />
          </Field>

          {error && (
            <p
              role="alert"
              className="rounded-xl bg-rose-50 px-3 py-2.5 text-sm text-rose-700 dark:bg-rose-950 dark:text-rose-300"
            >
              {error}
            </p>
          )}

          <Button type="submit" fullWidth loading={submitting}>
            {t('profile.setPassword.submit')}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <p className="muted text-xs">
            {t('profile.setPassword.signedInAs', {
              name: user?.name ?? '',
              code: user?.employeeCode ?? '',
            })}
          </p>

          {/* The only way out, so somebody who reached this on the wrong
              account is not stuck in it. */}
          <button onClick={logout} className="muted mt-2 min-h-11 text-sm underline">
            {t('profile.setPassword.notMe')}
          </button>
        </div>
      </div>
    </div>
  )
}
